import { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { VISITIQ_CATALOG_ID } from '~/components/AIInsightsUI/a2ui/operationsEnvelope';
import { pulseTopicOptionLabel } from '~/utils/pulse';

import { CardField, formatFiltersValue, isBooleanField, isStructuredField } from './confirmationFields';

const A2UI_VERSION = 'v0.9';
export const SUBMIT_ACTION_NAME = 'confirm_mutation';
export const CANCEL_ACTION_NAME = 'cancel_mutation';

interface DisplayFieldArgs {
  id: string;
  label: string;
  path: string;
  value: unknown;
}

interface FieldComponent {
  component: Record<string, unknown>;
  dataModelValue?: unknown;
  path?: string;
}

interface ActionLabels {
  confirm: string;
  cancel: string;
}

interface SurfaceArgs {
  fields: CardField[];
  surfaceId: string;
  labels: ActionLabels;
}

export const buildConfirmationSurface = ({ fields, surfaceId, labels }: SurfaceArgs): A2uiOperation[] | null => {
  if (fields.length === 0) return null;

  const dataModel: Record<string, unknown> = {};
  const submitContext: Record<string, unknown> = {};
  const childIds: string[] = [];
  const fieldComponents: Record<string, unknown>[] = [];

  fields.forEach((field, index) => {
    const id = `field-${index}`;
    const built = buildFieldComponent(field, id);
    fieldComponents.push(built.component);
    childIds.push(id);
    if (built.path !== undefined) {
      dataModel[field.name] = built.dataModelValue;
      submitContext[field.name] = { path: built.path };
    }
  });

  const components: Record<string, unknown>[] = [
    { id: 'root', component: 'Column', children: [...childIds, 'actions'] },
    ...fieldComponents,
    { id: 'actions', component: 'Row', justify: 'end', children: ['cancel', 'submit'] },
    {
      id: 'cancel',
      component: 'Button',
      child: 'cancel-text',
      action: { event: { name: CANCEL_ACTION_NAME, context: {} } },
    },
    { id: 'cancel-text', component: 'Text', text: labels.cancel },
    {
      id: 'submit',
      component: 'Button',
      variant: 'primary',
      child: 'submit-text',
      action: { event: { name: SUBMIT_ACTION_NAME, context: submitContext } },
    },
    { id: 'submit-text', component: 'Text', text: labels.confirm },
  ];

  return [
    { version: A2UI_VERSION, createSurface: { surfaceId, catalogId: VISITIQ_CATALOG_ID } },
    { version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: dataModel } },
    { version: A2UI_VERSION, updateComponents: { surfaceId, components } },
  ];
};

export const surfaceHasCancelAction = (operations: A2uiOperation[]): boolean =>
  operations.some((operation) => readSurfaceComponents(operation).some(isCancelComponent));

interface LocalizeArgs {
  operations: A2uiOperation[];
  labels: ActionLabels;
  fields: CardField[];
}

export const localizeSurface = ({ operations, labels, fields }: LocalizeArgs): A2uiOperation[] => {
  const actionTextIds = new Map<string, string>();
  operations.forEach((operation) =>
    readSurfaceComponents(operation).forEach((component) => {
      const actionName = readActionName(component);
      const childId = component.child;
      if (typeof childId !== 'string') return;
      if (actionName === SUBMIT_ACTION_NAME) actionTextIds.set(childId, labels.confirm);
      if (actionName === CANCEL_ACTION_NAME) actionTextIds.set(childId, labels.cancel);
    }),
  );
  const captionsByPath = new Map(fields.map((field) => [`/${field.name}`, fieldCaption(field)]));
  if (actionTextIds.size === 0 && captionsByPath.size === 0) return operations;

  return operations.map((operation) => {
    const update = asRecord(operation.updateComponents);
    const components = readSurfaceComponents(operation);
    if (!update || components.length === 0) return operation;
    return {
      ...operation,
      updateComponents: {
        ...update,
        components: components.map((component) => localizeComponent(component, actionTextIds, captionsByPath)),
      },
    };
  });
};

const localizeComponent = (
  component: Record<string, unknown>,
  actionTextIds: Map<string, string>,
  captionsByPath: Map<string, string>,
): Record<string, unknown> => {
  const actionText = typeof component.id === 'string' ? actionTextIds.get(component.id) : undefined;
  if (actionText !== undefined) return { ...component, text: actionText };

  const path = asRecord(component.value)?.path;
  const caption = typeof path === 'string' ? captionsByPath.get(path) : undefined;
  if (caption === undefined) return component;
  const captionKey = 'title' in component ? 'title' : 'label';
  return { ...component, [captionKey]: caption };
};

const readSurfaceComponents = (operation: A2uiOperation): Record<string, unknown>[] => {
  const update = asRecord(operation.updateComponents);
  const components = update?.components;
  return Array.isArray(components) ? components.filter(isRecord) : [];
};

const isCancelComponent = (component: Record<string, unknown>): boolean =>
  readActionName(component) === CANCEL_ACTION_NAME;

const readActionName = (component: Record<string, unknown>): string | null => {
  const name = asRecord(asRecord(component.action)?.event)?.name;
  return typeof name === 'string' ? name : null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fieldCaption = (field: CardField): string => field.label + (field.required ? ' *' : '');

const buildFieldComponent = (field: CardField, id: string): FieldComponent => {
  const path = `/${field.name}`;
  const label = fieldCaption(field);

  if (field.sensitive) {
    return { component: { id, component: 'VIQSecretInput', label, value: { path } }, dataModelValue: '', path };
  }
  if (isBooleanField(field)) {
    return {
      component: { id, component: 'VIQSwitch', title: label, value: { path } },
      dataModelValue: !!field.value,
      path,
    };
  }
  const displayBuilder = DISPLAY_FIELD_BUILDERS[field.display ?? ''] ?? DISPLAY_FIELD_BUILDERS[field.name];
  if (displayBuilder) return displayBuilder({ id, label, path, value: field.value });
  if (isStringArray(field.value) || field.stringArray) {
    const value = Array.isArray(field.value) ? field.value : [];
    return { component: { id, component: 'VIQMultiTagInput', label, value: { path } }, dataModelValue: value, path };
  }
  if (isStructuredField(field)) {
    return { component: { id, component: 'Text', text: `${label}: ${JSON.stringify(field.value)}` } };
  }
  const isNumber = field.schemaType === 'integer' || field.schemaType === 'number';
  return {
    component: { id, component: 'TextField', label, value: { path }, ...(isNumber ? { variant: 'number' } : {}) },
    dataModelValue: field.value == null ? '' : String(field.value),
    path,
  };
};

const buildPulseTopicsComponent = ({ id, label, path, value }: DisplayFieldArgs): FieldComponent => {
  const topicIds = isStringArray(value) ? value : [];
  return {
    component: {
      id,
      component: 'ChoicePicker',
      label,
      variant: 'multipleSelection',
      options: topicIds.map((topicId) => ({ label: pulseTopicOptionLabel(topicId), value: topicId })),
      value: { path },
    },
    dataModelValue: topicIds,
    path,
  };
};

const buildFiltersComponent = ({ id, label, value }: DisplayFieldArgs): FieldComponent => ({
  component: { id, component: 'Text', text: `${label}: ${formatFiltersValue(value)}` },
});

const DISPLAY_FIELD_BUILDERS: Record<string, (args: DisplayFieldArgs) => FieldComponent> = {
  pulseTopics: buildPulseTopicsComponent,
  filters: buildFiltersComponent,
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');
